import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes as ReactRoutes } from 'react-router-dom';

import { Routes } from './routeNames';
import { Splash } from '@/features/splash/SplashPage';
import { LoadingScreen } from '@/components/shared/LoadingScreen';
import { AuthGate } from '@/components/shared/AuthGate';
import { AddressGate } from '@/components/shared/AddressGate';
import { AppShell } from '@/features/shell/AppShell';
import { isTransactionalBrand } from '@/lib/brand';

const NotFoundPage = lazy(() => import('@/features/system/NotFoundPage'));

// Address picker — public, sits outside the tab shell (full-bleed).
const AddressPage = lazy(() => import('@/features/address/AddressPage'));

// Legal — public Terms & Conditions (UAT #11), outside the tab shell.
const TermsPage = lazy(() => import('@/features/legal/TermsPage'));

// Legal — STATIC public T&C + Privacy at stable URLs for App Store / Google
// Play submission (Ask 4). No backend/auth dependency.
const LegalTermsPage = lazy(() => import('@/features/legal/LegalTermsPage'));
const LegalPrivacyPage = lazy(
  () => import('@/features/legal/LegalPrivacyPage'),
);

// Auth pages — outside the tab shell.
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const OtpPage = lazy(() => import('@/features/auth/OtpPage'));
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'));
const OnboardingPage = lazy(() => import('@/features/auth/OnboardingPage'));

// Tab pages.
const HomePage = lazy(() => import('@/features/home/HomePage'));

// Hourly flows.
const HourlyServiceDetailPage = lazy(
  () => import('@/features/hourly/HourlyServiceDetailPage'),
);
const BookingWizardPage = lazy(() => import('@/features/hourly/BookingWizardPage'));
const CategoryPage = lazy(() => import('@/features/category/CategoryPage'));

// Bookings.
const BookingsListPage = lazy(() => import('@/features/bookings/BookingsListPage'));
const BookingDetailPage = lazy(() => import('@/features/bookings/BookingDetailPage'));

// Wallet (D2-customer — balance + history + Hyperpay self-service top-up).
const WalletPage = lazy(() => import('@/features/wallet/WalletPage'));

// Account.
const AccountPage = lazy(() => import('@/features/account/AccountPage'));
const ProfilePage = lazy(() => import('@/features/account/ProfilePage'));
const AddressesPage = lazy(() => import('@/features/account/AddressesPage'));
const SupportPage = lazy(() => import('@/features/support/SupportPage'));

// Payment (Hyperpay widget host + post-payment return).
const PayCheckoutPage = lazy(() => import('@/features/payment/PayCheckoutPage'));
const PayReturnPage = lazy(() => import('@/features/payment/PayReturnPage'));

// Location-confirm (H1 #9) — public tokenized map page, full-bleed (no shell).
const LocationConfirmPage = lazy(
  () => import('@/features/location-confirm/LocationConfirmPage'),
);

const ComingSoon = lazy(() => import('@/features/system/ComingSoonPage'));

// Root + catch-all for a `transactional` brand host (Taheel) — a short branded
// page explaining what the domain is for. See BrandGatePage's own header.
const BrandGatePage = lazy(() => import('@/features/system/BrandGatePage'));

/**
 * Route plan (M1 customer web):
 *
 *   /                        SplashPage           — always → /home
 *   /address                 AddressPage          — public, voluntary entry
 *   /login, /register, /otp  Auth pages           — public, no shell
 *   /onboarding              OnboardingPage       — requires auth
 *
 *   AppShell wraps the tab routes (responsive header + bottom tab bar):
 *     /home                  HomePage             — public (address chip)
 *     /services, /services/select  → /home        (legacy picker, retired
 *                            2026-05-24 — every service is on /home now)
 *     /services/hourly       HomePage             — public (alias)
 *     /services/hourly/:id   HourlyServiceDetail  — public
 *     /services/hourly/:id/book  BookingWizard    — AddressGate + AuthGate
 *     /category/:id          CategoryPage         — public
 *     /services/resident     ComingSoon           — public
 *     /bookings              BookingsListPage     — AuthGate (my bookings)
 *     /bookings/:id          BookingDetail        — AuthGate
 *     /wallet                ComingSoon           — AuthGate
 *     /account               AccountPage          — AuthGate
 *     /account/*             sub-routes           — AuthGate
 *
 * Browse-everywhere policy (2026-05-24): the catalog is address-optional.
 * Customers browse categories and service detail with no address pinned;
 * services render unfiltered until coords are present, at which point
 * the home + category pages switch to the zone-aware (servicesByArea)
 * query. AddressGate now only guards the booking wizard, where pricing
 * and slots truly require a resolved zone. AuthGate continues to guard
 * personal data and the booking commit.
 */
/**
 * Route tree for a `transactional` brand host — Taheel today.
 *
 * Exactly the surfaces a Taheel customer is SENT to, and nothing else:
 *
 *   /pay/:checkoutId         the HyperPay widget the app's WebView opens
 *   /pay/return              where HyperPay (or Tamara) bounces back
 *   /confirm-location/:token the operator-SMSed map pin
 *   /legal/terms|/privacy    the stable store-submission URLs
 *   /terms                   the backend-fed T&C the app links to
 *   everything else          BrandGatePage
 *
 * ── Why the rest is cut rather than hidden ────────────────────────────────
 * The remaining pages are not merely unbranded — they are Luvelle's product.
 * The catalog calls the Luvelle services endpoints, the wizard prices against
 * the Luvelle zone model, the account pages read Luvelle's customer record.
 * Taheel's equivalents live in the mobile app and its own BFF
 * (`watanhr-taheel-api`), which this web app does not talk to at all. A Taheel
 * customer reaching /home would get a women's home-spa catalog they cannot
 * book — so the route does not exist on this host.
 *
 * Deep links (`/t/*`) and `/.well-known/*` never reach React: the CDN rewrites
 * them to the backend ABOVE the SPA catch-all (see the deep-link brand-domain
 * checklist). They are absent here by design, not by omission.
 */
function TransactionalRouter() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <ReactRoutes>
        {/* Payment — the reason this host is pointed at by the app. */}
        <Route path={Routes.payCheckout.path} element={<PayCheckoutPage />} />
        <Route path={Routes.payReturn.path} element={<PayReturnPage />} />

        {/* Location-confirm — the reason this host is SMSed to customers. */}
        <Route
          path={Routes.confirmLocation.path}
          element={<LocationConfirmPage />}
        />

        {/* Legal — kept because the app and the store listings link to them,
         *  and because the payment page's footer does. No backend/auth
         *  dependency on the two /legal/* pages, so a store reviewer opening
         *  them on the Taheel domain always gets a 200. */}
        <Route path={Routes.terms.path} element={<TermsPage />} />
        <Route path={Routes.legalTerms.path} element={<LegalTermsPage />} />
        <Route path={Routes.legalPrivacy.path} element={<LegalPrivacyPage />} />

        {/* Root and every other path — including any Luvelle URL someone
         *  pastes onto this domain. */}
        <Route path="*" element={<BrandGatePage />} />
      </ReactRoutes>
    </Suspense>
  );
}

export function AppRouter() {
  // Decided by hostname, frozen at module init — a Taheel host never renders
  // the Luvelle consumer app, and an unrecognised host is Luvelle as before.
  if (isTransactionalBrand) return <TransactionalRouter />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <ReactRoutes>
        {/* Public, no shell. */}
        <Route path={Routes.splash.path} element={<Splash />} />
        <Route path={Routes.address.path} element={<AddressPage />} />
        <Route path={Routes.terms.path} element={<TermsPage />} />
        <Route path={Routes.legalTerms.path} element={<LegalTermsPage />} />
        <Route path={Routes.legalPrivacy.path} element={<LegalPrivacyPage />} />
        <Route path={Routes.login.path} element={<LoginPage />} />
        <Route path={Routes.otp.path} element={<OtpPage />} />
        <Route path={Routes.register.path} element={<RegisterPage />} />
        <Route
          path={Routes.onboarding.path}
          element={
            <AuthGate>
              <OnboardingPage />
            </AuthGate>
          }
        />

        {/* All tab routes share the responsive AppShell (header + bottom
         * tab bar). Shell renders <Outlet /> for the matched child. */}
        <Route element={<AppShell />}>
          {/* Public browse-everywhere catalog (address-optional). The
           *  pages themselves switch from unfiltered → area-filtered
           *  data as soon as the customer pins an address. */}
          <Route path={Routes.home.path} element={<HomePage />} />
          {/* Legacy picker routes — redirect to /home, which now hosts
           *  the full service catalog. */}
          <Route path="/services" element={<Navigate to="/home" replace />} />
          <Route
            path="/services/select"
            element={<Navigate to="/home" replace />}
          />
          <Route path={Routes.hourlyBrowse.path} element={<HomePage />} />
          <Route
            path={Routes.hourlyServiceDetail.path}
            element={<HourlyServiceDetailPage />}
          />
          <Route path={Routes.category.path} element={<CategoryPage />} />
          {/* Booking commit requires a resolved address (for pricing +
           *  slots) AND auth (for the commit itself). */}
          <Route
            path={Routes.hourlyBookingWizard.path}
            element={
              <AddressGate>
                <AuthGate>
                  <BookingWizardPage />
                </AuthGate>
              </AddressGate>
            }
          />
          {/* Multi-service entry point — same wizard, no pre-seeded
           *  service. The customer picks one or more services in
           *  Step 2. Address + Auth gates identical to the deep-link
           *  route above. */}
          <Route
            path={Routes.bookingWizardOpen.path}
            element={
              <AddressGate>
                <AuthGate>
                  <BookingWizardPage />
                </AuthGate>
              </AddressGate>
            }
          />

          {/* Auth-gated personal data. */}
          <Route
            path={Routes.bookings.path}
            element={
              <AuthGate>
                <BookingsListPage />
              </AuthGate>
            }
          />
          <Route
            path={Routes.bookingDetail.path}
            element={
              <AuthGate>
                <BookingDetailPage />
              </AuthGate>
            }
          />
          {/* Wallet (D2-customer, 2026-06-01) — balance + transaction history
           *  + self-service Hyperpay top-up. Auth-gated; the page handles its
           *  own zero-balance / not-linked states. Top-up reuses the existing
           *  /pay widget host. Backend gates the endpoints behind
           *  CUSTOMER_WALLET_ENABLED (404 when off → page shows load error). */}
          <Route
            path={Routes.wallet.path}
            element={
              <AuthGate>
                <WalletPage />
              </AuthGate>
            }
          />
          <Route
            path={Routes.account.path}
            element={
              <AuthGate>
                <AccountPage />
              </AuthGate>
            }
          />
          <Route
            path={Routes.profile.path}
            element={
              <AuthGate>
                <ProfilePage />
              </AuthGate>
            }
          />
          <Route
            path={Routes.addresses.path}
            element={
              <AuthGate>
                <AddressesPage />
              </AuthGate>
            }
          />

          {/* Coming-soon placeholders inside the shell. */}
          <Route
            path={Routes.visitTracking.path}
            element={<ComingSoon feature="visit-tracking" />}
          />
          <Route
            path={Routes.rateVisit.path}
            element={<ComingSoon feature="rate-visit" />}
          />
          {/* Wallet sub-routes hidden 2026-05-24 alongside /wallet itself. */}
          <Route
            path={Routes.paymentHistory.path}
            element={<Navigate to={Routes.home.path} replace />}
          />
          <Route
            path={Routes.loyalty.path}
            element={<Navigate to={Routes.home.path} replace />}
          />
          <Route
            path={Routes.subscriptions.path}
            element={<Navigate to={Routes.home.path} replace />}
          />
          <Route
            path={Routes.referral.path}
            element={<Navigate to={Routes.home.path} replace />}
          />
          <Route
            path={Routes.favorites.path}
            element={<ComingSoon feature="favorites" />}
          />
          <Route
            path={Routes.support.path}
            element={
              <AuthGate>
                <SupportPage />
              </AuthGate>
            }
          />
          <Route
            path={Routes.notifications.path}
            element={<ComingSoon feature="notifications" />}
          />
          <Route
            path={Routes.residentBrowse.path}
            element={<ComingSoon feature="resident-browse" />}
          />
          <Route
            path={Routes.workerProfile.path}
            element={<ComingSoon feature="worker-profile" />}
          />
          <Route
            path={Routes.applicationWizard.path}
            element={<ComingSoon feature="application-wizard" />}
          />
        </Route>

        {/* Payment — public, full-bleed (NO AppShell). Customer comes
         *  here straight from the booking wizard with the checkoutId in
         *  the path. The pages handle their own layout so the Hyperpay
         *  widget has the entire viewport. */}
        <Route path={Routes.payCheckout.path} element={<PayCheckoutPage />} />
        <Route path={Routes.payReturn.path} element={<PayReturnPage />} />

        {/* Location-confirm — public, full-bleed (NO AppShell). The customer
         *  opens this from an operator-sent SMS, pins their location, submits. */}
        <Route
          path={Routes.confirmLocation.path}
          element={<LocationConfirmPage />}
        />

        {/* Legacy `/en/` Astro path redirects to language picker */}
        <Route path="/en" element={<Navigate to="/?lang=en" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </ReactRoutes>
    </Suspense>
  );
}
